/* =========================================================
   PRIME CUTS — Bulk Reserve controller
   Shared state machine for the reserve flow. Loaded by every
   reserve-* section; the guard below makes duplicate loads a
   no-op.

   Sections register through the queue rather than touching
   PCReserve directly, because a section's inline script parses
   before this deferred asset runs:

     (window.PCReserveQueue = window.PCReserveQueue || []).push(
       function (R) { ...  }
     );
   ========================================================= */

(function () {
  if (window.PCReserve) return;

  var DRAFT_KEY = 'primecuts:bulkDraft';

  /* ---------- State ---------- */
  var state = {
    step: 1,
    type: null,          // beef | pork | combo
    beefSizeId: null,
    porkSizeId: null,
    activeCutTab: 'beef',
    beef: { presetId: null, advanced: {}, advancedOpen: false, notes: '' },
    pork: { presetId: null, advanced: {}, advancedOpen: false, notes: '' },
    fulfillment: null,
    contact: {},
    groupId: null
  };

  try {
    var raw = localStorage.getItem(DRAFT_KEY);
    if (raw) {
      var saved = JSON.parse(raw);
      Object.keys(saved).forEach(function (k) {
        if (saved[k] && typeof saved[k] === 'object' && !Array.isArray(saved[k]) && state[k]) {
          Object.keys(saved[k]).forEach(function (j) { state[k][j] = saved[k][j]; });
        } else {
          state[k] = saved[k];
        }
      });
    }
  } catch (e) {}

  function save() {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(state)); } catch (e) {}
  }
  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
  }

  /* ---------- Config, pushed in by each section ---------- */
  var config = {};

  function setConfig(key, value) {
    config[key] = value;
    emit('config:' + key, value);
  }
  function getConfig(key, fallback) {
    return config[key] === undefined ? fallback : config[key];
  }

  /* Read a <script type="application/json"> payload emitted by a section. */
  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---------- Tiny event bus ---------- */
  var listeners = {};

  function on(evt, fn) {
    (listeners[evt] = listeners[evt] || []).push(fn);
  }
  function emit(evt, data) {
    (listeners[evt] || []).forEach(function (fn) {
      try { fn(data); } catch (e) { console.error('[PCReserve]', evt, e); }
    });
  }

  /* ---------- Step validation ---------- */
  /* addValidator(step, fn) — fn returns true, or a message string. */
  var validators = {};

  function addValidator(step, fn) {
    validators[step] = fn;
  }

  /* ---------- Navigation ---------- */
  function validateStep(n) {
    var check = validators[n];
    if (!check) return true;
    var result = check();
    if (result !== true) {
      toast(typeof result === 'string' ? result : 'Finish this step first');
      return false;
    }
    return true;
  }

  function goToStep(n) {
    /* A nav pointing at a step whose section was never placed would
       otherwise hide every panel and leave the customer on a blank page. */
    if (!document.querySelector('[data-pc-panel="' + n + '"]')) {
      console.warn('[PCReserve] no section renders step ' + n + '; staying on step ' + state.step);
      return false;
    }

    if (n > state.step) {
      for (var s = state.step; s < n; s++) {
        if (!validateStep(s)) return false;
      }
    }

    state.step = n;
    save();
    syncChrome();
    emit('step', n);
    scrollToTop();
    return true;
  }

  function syncChrome() {
    document.querySelectorAll('[data-pc-panel]').forEach(function (panel) {
      panel.classList.toggle('active', Number(panel.dataset.pcPanel) === state.step);
    });
    document.querySelectorAll('[data-pc-step]').forEach(function (step) {
      var i = Number(step.dataset.pcStep);
      step.classList.toggle('active', i === state.step);
      step.classList.toggle('done', i < state.step);
    });
  }

  function scrollToTop() {
    var anchor = document.querySelector('[data-pc-stepper]') || document.querySelector('[data-pc-panel]');
    if (!anchor) return;
    var top = anchor.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  /* ---------- Toast ---------- */
  var toastEl = null;
  var toastTimer = null;

  function toast(message, isError) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pc-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.className = 'pc-toast show' + (isError ? ' error' : '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.className = 'pc-toast'; }, 2200);
  }

  /* ---------- Money helpers ---------- */
  function money(cents) {
    return '$' + Math.round(cents / 100).toLocaleString('en-US');
  }
  function dollars(amount) {
    return '$' + Math.round(amount).toLocaleString('en-US');
  }

  /* ---------- Order maths ----------
     Reads the configs the step sections register: `sizes` and
     `comboTiers` from step 1, `cut:beef` / `cut:pork` from step 2,
     and `fulfillment` from step 3. Shared by steps 3 and 4 so the
     figures on screen and the figures sent to the cart cannot drift. */

  function findSize(animal, id) {
    var sizes = config.sizes || [];
    if (!animal || !id) return null;
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i].animal === animal && sizes[i].id === id) return sizes[i];
    }
    return null;
  }

  function comboDiscount(beefId, porkId) {
    var tiers = config.comboTiers || [];
    if (!beefId || !porkId) return 0;
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var beefOk = t.beef === 'any' || t.beef === beefId;
      var porkOk = t.pork === 'any' || t.pork === porkId;
      if (beefOk && porkOk) return t.amount;
    }
    return 0;
  }

  /* Priced sausage picks for one animal, as [{ id, name, price }]. */
  function addOnsFor(animal) {
    var meta = config['cut:' + animal];
    var branch = state[animal];
    if (!meta || !branch || !branch.advanced) return [];
    var picked = branch.advanced[meta.sausageKey] || [];
    var out = [];
    picked.forEach(function (id) {
      var item = meta.sausage[id];
      if (item && item.price > 0) out.push({ id: id, name: item.name, price: item.price });
    });
    return out;
  }

  function fulfillmentOption() {
    var options = config.fulfillment || [];
    for (var i = 0; i < options.length; i++) {
      if (options[i].key === state.fulfillment) return options[i];
    }
    return null;
  }

  function summary() {
    var wantsBeef = state.type === 'beef' || state.type === 'combo';
    var wantsPork = state.type === 'pork' || state.type === 'combo';

    var beef = wantsBeef ? findSize('beef', state.beefSizeId) : null;
    var pork = wantsPork ? findSize('pork', state.porkSizeId) : null;

    var lines = [];
    var subtotal = 0;
    var deposit = 0;

    if (beef) {
      lines.push({ kind: 'item', label: beef.name, amount: beef.price });
      subtotal += beef.price;
      deposit += beef.deposit;
    }
    if (pork) {
      lines.push({ kind: 'item', label: pork.name, amount: pork.price });
      subtotal += pork.price;
      deposit += pork.deposit;
    }

    var addOns = [];
    if (beef) addOns = addOns.concat(addOnsFor('beef'));
    if (pork) addOns = addOns.concat(addOnsFor('pork'));
    addOns.forEach(function (a) {
      lines.push({ kind: 'addon', label: a.name, amount: a.price });
      subtotal += a.price;
    });

    var option = fulfillmentOption();
    var fee = option && option.fee ? option.fee : 0;
    if (fee > 0) {
      lines.push({ kind: 'fee', label: option.label, amount: fee });
      subtotal += fee;
    }

    var discount = 0;
    if (state.type === 'combo' && beef && pork) {
      discount = comboDiscount(beef.id, pork.id);
    }

    var total = subtotal - discount;

    return {
      beef: beef,
      pork: pork,
      lines: lines,
      addOns: addOns,
      fulfillment: option,
      fee: fee,
      discount: discount,
      subtotal: subtotal,
      total: total,
      deposit: deposit,
      balance: total - deposit,
      complete: !!(beef || pork)
    };
  }

  /* ---------- Summary rendering ----------
     labels: { title, discount, deposit, balance, empty } */
  function renderSummary(el, labels) {
    if (!el) return;
    labels = labels || {};
    var data = summary();

    if (!data.complete) {
      el.innerHTML = '<div class="pcard-lg-head"><h3>' + escapeHTML(labels.title || '') + '</h3></div>' +
        '<p class="pc-summary-fine">' + escapeHTML(labels.empty || '') + '</p>';
      return;
    }

    var html = '<div class="pcard-lg-head"><h3>' + escapeHTML(labels.title || '') + '</h3></div>';

    data.lines.forEach(function (line) {
      html += '<div class="pc-summary-line">' +
        '<span class="lbl">' + escapeHTML(line.label) + '</span>' +
        '<span class="val">' + dollars(line.amount) + '</span></div>';
    });

    if (data.discount > 0) {
      html += '<div class="pc-summary-line discount">' +
        '<span class="lbl">' + escapeHTML(labels.discount || '') + '</span>' +
        '<span class="val">−' + dollars(data.discount) + '</span></div>';
    }

    html += '<div class="pc-summary-total-row">' +
      '<span class="lbl">' + escapeHTML(labels.deposit || '') + '</span>' +
      '<span class="val">' + dollars(data.deposit) + '</span></div>';

    if (labels.balance) {
      html += '<p class="pc-summary-fine">' +
        escapeHTML(labels.balance.replace('[balance]', dollars(data.balance)).replace('[total]', dollars(data.total))) +
        '</p>';
    }

    el.innerHTML = html;
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- Cart handoff ----------
     One line item per animal. Order-level details are repeated on every
     line rather than written once, so removing a line in the cart can
     never strip the contact or fulfilment details from the other one. */

  function groupId() {
    if (!state.groupId) {
      state.groupId = 'RES-' + Math.random().toString(36).slice(2, 7).toUpperCase();
      save();
    }
    return state.groupId;
  }

  function buildCartItems(labels) {
    labels = labels || {};
    var data = summary();
    if (!data.complete) return [];

    var group = groupId();
    var shared = {};
    shared[labels.reservation || 'Reservation'] = group;

    /* Every contact field becomes its own property, named with the label
       the merchant typed in step 3. Address fields only when delivering. */
    var needsAddress = !!(data.fulfillment && data.fulfillment.needsAddress);
    (config.contactFields || []).forEach(function (field) {
      if (field.group === 'address' && !needsAddress) return;
      var value = String(state.contact[field.key] || '').trim();
      if (value) shared[field.label || field.key] = value;
    });

    if (data.fulfillment) {
      shared[labels.fulfillment || 'Fulfillment'] = data.fee > 0
        ? data.fulfillment.label + ' (+' + dollars(data.fee) + ')'
        : data.fulfillment.label;
    }
    if (data.discount > 0) {
      shared[labels.discount || 'Combo Discount'] = '-' + dollars(data.discount);
    }
    shared[labels.total || 'Order Total'] = dollars(data.total);
    shared[labels.deposit || 'Deposit Today'] = dollars(data.deposit);
    shared[labels.balance || 'Balance at Pickup'] = dollars(data.balance);

    var items = [];
    [['beef', data.beef], ['pork', data.pork]].forEach(function (pair) {
      var animal = pair[0];
      var size = pair[1];
      if (!size || !size.variantId) return;

      var props = {};
      Object.keys(shared).forEach(function (k) { props[k] = shared[k]; });

      if (size.takeHome) props[labels.takehome || 'Take-Home'] = size.takeHome;
      if (size.freezer) props[labels.freezer || 'Freezer Space'] = size.freezer;

      var meta = config['cut:' + animal];
      var branch = state[animal] || {};
      var adv = branch.advanced || {};

      if (meta) {
        if (meta.presetNames[branch.presetId]) {
          props[labels.preset || 'Cutting Preset'] = meta.presetNames[branch.presetId];
        }

        /* One property per radio group, using the merchant's own label
           and option wording. Sub-pickers carry no label of their own and
           are folded into the specialty list below instead. */
        Object.keys(meta.fields).forEach(function (key) {
          var field = meta.fields[key];
          if (!field.label) return;
          var value = adv[key];
          if (value == null || value === '') return;
          props[field.label] = field.options[value] || value;
        });

        var specialty = (adv.specialty || []).map(function (id) {
          var item = meta.specialty[id];
          if (!item) return null;
          var name = item.name;
          if (item.subField && adv[item.subField]) {
            var sub = meta.fields[item.subField];
            var subLabel = sub && sub.options[adv[item.subField]];
            if (subLabel) name += ' (' + subLabel + ')';
          }
          return name;
        }).filter(Boolean);
        if (specialty.length) props[labels.specialty || 'Specialty Cuts'] = specialty.join(', ');

        var sausage = (adv[meta.sausageKey] || []).map(function (id) {
          var item = meta.sausage[id];
          if (!item) return null;
          return item.price > 0 ? item.name + ' (+' + dollars(item.price) + ')' : item.name;
        }).filter(Boolean);
        if (sausage.length) props[labels.sausage || 'Sausage Add-Ons'] = sausage.join(', ');
      }

      if (branch.notes) props[labels.notes || 'Butcher Notes'] = branch.notes;

      /* Underscore-prefixed properties stay out of the cart and checkout
         display but remain readable by the theme and the API. */
      props._pc_kind = 'reservation';
      props._pc_group = group;
      /* Numeric copies for the cart footer. The visible Balance property is
         a formatted string repeated on every line of a combo, so the footer
         dedupes on _pc_group and sums these instead of the display strings. */
      props._pc_balance = String(data.balance);
      props._pc_order_total = String(data.total);
      props._pc_spec = JSON.stringify({
        animal: animal,
        size: size.id,
        preset: branch.presetId,
        advanced: adv,
        notes: branch.notes || ''
      });

      items.push({ id: size.variantId, quantity: 1, properties: props });
    });

    return items;
  }

  function addToCart(labels) {
    var items = buildCartItems(labels);
    if (!items.length) {
      return Promise.reject(new Error('Nothing to add: no reservation product resolved.'));
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok) {
          throw new Error(body.description || body.message || 'Cart rejected the request.');
        }
        return body;
      });
    });
  }

  /* ---------- Public API ---------- */
  var PCReserve = {
    state: state,
    config: config,
    save: save,
    clearDraft: clearDraft,
    setConfig: setConfig,
    getConfig: getConfig,
    readJSON: readJSON,
    on: on,
    emit: emit,
    addValidator: addValidator,
    validateStep: validateStep,
    goToStep: goToStep,
    buildCartItems: buildCartItems,
    addToCart: addToCart,
    syncChrome: syncChrome,
    toast: toast,
    money: money,
    dollars: dollars,
    findSize: findSize,
    comboDiscount: comboDiscount,
    addOnsFor: addOnsFor,
    fulfillmentOption: fulfillmentOption,
    summary: summary,
    renderSummary: renderSummary,
    escapeHTML: escapeHTML
  };

  window.PCReserve = PCReserve;

  /* ---------- Drain the registration queue ---------- */
  var queue = window.PCReserveQueue || [];
  function run(fn) {
    try { fn(PCReserve); } catch (e) { console.error('[PCReserve] registration', e); }
  }
  queue.forEach(run);
  window.PCReserveQueue = { push: run };

  /* ---------- Boot ---------- */
  function boot() {
    /* URL preselection: ?type=beef&size=quarter from the bulk landing page. */
    var params = new URLSearchParams(window.location.search);
    var urlType = params.get('type');
    var urlSize = params.get('size');
    if (urlType && ['beef', 'pork', 'combo'].indexOf(urlType) > -1) state.type = urlType;
    if (urlSize) {
      if (urlType === 'beef') state.beefSizeId = urlSize;
      else if (urlType === 'pork') state.porkSizeId = urlSize;
    }
    if (urlType || urlSize) {
      state.step = 1;
      save();
    }

    /* A saved draft can point at a step whose section is not on the page,
       either mid-build or because the merchant removed it. Fall back
       rather than rendering an empty flow. */
    if (!document.querySelector('[data-pc-panel="' + state.step + '"]')) {
      state.step = 1;
      save();
    }

    syncChrome();
    emit('boot');
    emit('step', state.step);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
